import { useEffect, useState } from 'react';
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { IconPlus } from '@tabler/icons-react';
import { v4 as uuidv4 } from 'uuid';
import ScreeningMultiAndSingleSelectItem from './ScreeningMultiAndSingleSelectItem';
import useWorkspaceRoles from '@/hooks/useWorkspaceRoles';
import { Button } from '@/components/ui/button';
import { MultiSelectAnswerProps } from '@/types';
import useTranslation from '@/hooks/useTranslation';
import RequiredLabel from '@/components/labels/RequiredLabel';
import { Checkbox } from '@/components/ui/checkbox';

const MAX_ANSWERS = 20;

function ScreeningMultiAndSingleSelectAnswer({
  questionId,
  responses,
  isProjectCompleted,
  isProjectPublished,
  itemType,
  handleQuestionAnswerChange,
  clearAnswerError,
  handleDeleteAnswer,
  handleAnswersOrderChange,
  setIsFormChanged,
  error,
  randomize,
  onRandomizeAnswersChange,
  quotasEnabled,
  onQuotaPercentageChange,
}: MultiSelectAnswerProps) {
  const [answers, setAnswers] = useState(responses);
  const [isActiveDrag, setIsActiveDrag] = useState(false);
  const [openQuestionId, setOpenQuestionId] = useState<string | null>(null);
  const { isUserViewer } = useWorkspaceRoles();
  const { lang } = useTranslation();

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!active || !over || active.id === over.id) return;

    setIsFormChanged(true);

    setAnswers((prevAnswers) => {
      const activeIndex = prevAnswers.findIndex((q) => q.id === active.id);
      const overIndex = prevAnswers.findIndex((q) => q.id === over.id);
      const reorderedAnswers = arrayMove(prevAnswers, activeIndex, overIndex);
      handleAnswersOrderChange(questionId, reorderedAnswers);
      return reorderedAnswers;
    });

    setIsActiveDrag(false);
  };

  const addAnswer = () => {
    const newAnswer = {
      id: uuidv4(),
      something: 'something',
      value: '',
      rule: null,
    };

    // Update the state
    setAnswers((prevAnswers) => [...(prevAnswers || []), newAnswer]);
    setIsFormChanged(true);
    handleQuestionAnswerChange(questionId, newAnswer.id, newAnswer.value, newAnswer.rule);
  };

  const handleDeleteQuestion = (idToDelete: string) => {
    // Update the state
    setAnswers((prevAnswer) => prevAnswer.filter((question) => question.id !== idToDelete));

    handleDeleteAnswer(questionId, idToDelete);
  };

  useEffect(() => {
    setAnswers(responses);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responses]);

  const isDisabled = isUserViewer || isProjectPublished || isProjectCompleted;
  const isButtonDisabled = isDisabled || answers?.length >= MAX_ANSWERS;
  return (
    <div className="flex flex-col w-full h-full">
      <div className="flex items-center w-full h-full">
        <div className="flex flex-col w-full h-full">
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            onDragStart={() => setIsActiveDrag(true)}
          >
            <div className="flex flex-col w-full h-full gap-3 p-6 mt-2 mb-5 bg-gray-100 rounded-md">
              <SortableContext
                disabled={isDisabled}
                items={answers}
                strategy={verticalListSortingStrategy}
              >
                {answers?.map((answer, index, arr) => (
                  <ScreeningMultiAndSingleSelectItem
                    answer={answer}
                    isActiveDrag={isActiveDrag}
                    key={answer.id}
                    id={answer.id}
                    questionId={questionId}
                    openQuestionId={openQuestionId}
                    setOpenQuestionId={setOpenQuestionId}
                    isProjectCompleted={isProjectCompleted}
                    isProjectPublished={isProjectPublished}
                    isUserViewer={isUserViewer}
                    index={index}
                    handleDeleteQuestion={handleDeleteQuestion}
                    handleQuestionAnswerChange={handleQuestionAnswerChange}
                    clearAnswerError={clearAnswerError}
                    itemType={itemType}
                    totalNumberOfAnswers={arr?.length}
                    error={error?.[index]}
                    quotasEnabled={quotasEnabled}
                    onQuotaPercentageChange={onQuotaPercentageChange}
                  />
                ))}
              </SortableContext>
            </div>
          </DndContext>
          <div className="flex items-center justify-between w-full mt-5">
            <Button
              type="button"
              onClick={addAnswer}
              disabled={isButtonDisabled}
              variant="default"
              className="flex items-center self-start gap-1 text-xs"
            >
              <IconPlus size={13} />
              {lang.get('msg.addNewAnswer')}
            </Button>
            <div className="flex items-center">
              <RequiredLabel
                title="randomizeAnswers"
                notRequired
                className="flex flex-row items-center"
              >
                <Checkbox
                  className="w-4 h-4"
                  checked={randomize}
                  onCheckedChange={() => {
                    setIsFormChanged(true);
                    onRandomizeAnswersChange(questionId, !randomize);
                  }}
                  disabled={isDisabled}
                />
              </RequiredLabel>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ScreeningMultiAndSingleSelectAnswer;
